/**
 * useEchDyn — assembles the `EchCtx` that `echDyn.ts` resolves against, from the
 * worker feeds the monolith reads out of page globals.
 *
 * The monolith's resolver reaches straight into the DOM and into globals
 * (`$("divisionCode")`, `window.__commSel`, `__divContent`, `__socLinks`, `__lvCC`,
 * `__ADDR`, `__REC_IDS`). None of those exist here, so this hook is the seam: it
 * owns the fetching and caching, and hands `echDynVal()` a plain object. The
 * VALUES and the precedence order are the monolith's; only the plumbing differs.
 *
 * Five feeds, all already in services/api.ts:
 *   /sfmc-division-content → the division's name + phone (the phone fallback at
 *                            EVERY level, because a division phone is always
 *                            answerable)
 *   /sfmc-social-links     → per-division handles, national LNR as the fallback
 *   /refdata               → per-pick Address/City/State/Zip + pageUrl, by record id
 *   /sfmc-content          → the single picked community's plans + homesites + WHC
 *   /sfmc-isc              → the division's OWN Contact Us markup, ISC photo and all
 *
 * `pending` is true while any of them is in flight. It is what stops Dynamic
 * writing an empty value over real copy in the second before a feed answers.
 */
import { useEffect, useMemo, useState } from "react";
import type { DivisionContent, SocialLinkRow, RefdataResponse } from "./api";
import { fetchDirect } from "./echDirect";
/* ── EVERY FEED GOES THROUGH THE PAGE-LEVEL PROMISE CACHE (2026-08-20) ─────────
   Not `./api` directly any more. This hook is now mounted by the FORM as well as
   the Studio (the preview rail fills from Step 1), and App renders exactly one of
   those views at a time — so opening or closing the Studio unmounts one instance
   and mounts the other, and every mount used to re-issue all five routes. The
   dedupe is in the service so it outlives the component; see echFeeds.ts. */
import {
  feedDivisionContent as getDivisionContent,
  feedSocialLinks as getSocialLinks,
  feedRefdata as getRefdata,
  feedCommunityPack as getCommunityPack,
  feedCommunitySearch as searchCommunities,
  feedIscBlock as getIscBlock,
  feedDeRow as getDeRow,
} from "./echFeeds";
import type { CommunityPackResponse } from "../types/api";
import type { EchCtx, EchPick, DeRowState } from "./echDyn";
import { DE_FAILED, echAllDataSources, echBorrowAddr, echBorrowAmenities, echCollectionsOf, echCommsInAoi, echNameVariants, echParentName, echSharedParent, echSharedParentUrl, echStateOfUrl } from "./echDyn";
import { echVersion } from "./echRegistry";
import type { Pack } from "./dynamicFill";
import { divMarkets } from "./snapshots";
import { echDivisionStates, echStateAllowed } from "./echGeo";
import { MARKETS, MARKET_STATE } from "./requestData";

/** what Step 1 knows — the only input this hook takes from the form */
export interface Step1Scope {
  /** Initiative Request Focus: Division | Market | Area of Interest | Community */
  focus: string;
  divisionCode: string;
  /** comma-joined community names, as FormState stores them */
  community?: string;
  masterplan?: string;
  areaOfInterest?: string;
  /** the market label(s) — "ATL - Atlanta" */
  market?: string | string[];
  /** carried so a fill re-runs on an initiative change (see EchCtx.initiative) */
  initiative?: string;
}

const names = (s?: string): string[] =>
  String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

/** array-or-map tolerant: the worker has shipped both shapes for social links */
function socMap(rows: SocialLinkRow[] | Record<string, SocialLinkRow> | undefined): Record<string, Record<string, string | undefined>> {
  const out: Record<string, Record<string, string | undefined>> = {};
  if (!rows) return out;
  const list = Array.isArray(rows) ? rows : Object.keys(rows).map((k) => ({ ...rows[k], code: rows[k].code || k }));
  for (const r of list) {
    const code = String(r.code || "").toUpperCase();
    if (!code) continue;
    out[code] = { fb: r.fb, x: r.x, ig: r.ig, pin: r.pin, yt: r.yt, li: r.li };
  }
  return out;
}

/**
 * ONE FETCH PER DIVISION, FOR THE LIFE OF THE PAGE — the monolith's
 * `window.__iscHtml` / `__iscTried` pair (index.html L11682–11683), which are page
 * globals for exactly this reason: the Studio unmounts and remounts (closing and
 * reopening it), and a `useState` cache would re-fetch the same division's block
 * every time. Module scope is this repo's equivalent, and it is also what keeps
 * `pending` honest — a division already in the cache never raises inflight at all.
 *
 * A key present with "" means "asked, nothing usable came back": that is a
 * terminal answer, not a retry cue, so the block keeps its generic cream fallback
 * rather than flickering between the two.
 */
const ISC_CACHE: Record<string, string> = {};

export function useEchDyn(s: Step1Scope): EchCtx {
  const [divRows, setDivRows] = useState<DivisionContent[] | null>(null);
  const [social, setSocial] = useState<Record<string, Record<string, string | undefined>> | null>(null);
  const [ref, setRef] = useState<{ div: string; data: RefdataResponse } | null>(null);
  const [pack, setPack] = useState<{ key: string; C: CommunityPackResponse | null } | null>(null);
  const [isc, setIsc] = useState<{ div: string; html: string } | null>(null);
  /* DE rows, keyed `<externalKey>` for ONE division — three states, see the effect below
     and `EchCtx.deRows`: a row, `null` (answered: no row), or `DE_FAILED` (the route
     failed). The container being `null` means no division / no declared source. */
  const [deRows, setDeRows] = useState<{ div: string; rows: Record<string, DeRowState> } | null>(null);
  const [inflight, setInflight] = useState(0);
  /** name → community code, resolved once per name (monolith __lvNC). State, not a
   *  ref: resolving a code has to re-derive `picks`, and a ref can't do that. */
  const [codes, setCodes] = useState<Record<string, string>>({});
  /* ── A MASTERPLAN THE SEARCH KNOWS ONLY THROUGH ITS COLLECTIONS (2026-09-11) ─────
     Driven live on dev, JAX → "Brightlyn at SilverLeaf" (Coming Soon): refdata has no
     record for the masterplan OR its three collections (a brand-new community is not
     in the Airtable tables yet), so the pick carried no pageUrl, `echCollectionsOf`
     found nothing, no pack resolved — and every CTA on the email rendered `href=""`
     (Uche's blank Join Interest List / View Community on a masterplan Coming Soon,
     2026-09-10 call). The SFMC name search DID answer: three hits, "Brightlyn at
     SilverLeaf 40s/50s/60s", each with a lennar.com url under
     …/st-johns/brightlyn-at-silverleaf/<collection>. The exact-hit rule (correctly)
     refuses to pick one of them as THE community — but together they name the
     masterplan: a common parent path whose last slug IS the pick's name. That parent
     is the masterplan's page (it is `meta-Page: MPC` on lennar.com and carries the
     #join-interest-list anchor), and the hits are its collections. Keyed by the pick
     name so a change of pick never reuses it. */
  const [mpDerived, setMpDerived] = useState<{ key: string; url: string; kids: { code: string; name: string }[] } | null>(null);

  const bump = (n: number) => setInflight((v) => Math.max(0, v + n));

  /* division content + social links: division-independent fetches, once each */
  useEffect(() => {
    bump(2);
    getDivisionContent().then((d) => setDivRows(d.items || [])).catch(() => setDivRows([])).finally(() => bump(-1));
    getSocialLinks().then((d) => setSocial(socMap(d.divisions))).catch(() => setSocial({})).finally(() => bump(-1));
  }, []);

  /* /refdata is division-scoped — re-fetch on a division change, never merge two */
  useEffect(() => {
    const div = s.divisionCode;
    if (!div) { setRef(null); return; }
    let live = true;
    bump(1);
    getRefdata(div)
      .then((d) => { if (live) setRef({ div, data: d || {} }); })
      .catch(() => { if (live) setRef({ div, data: {} }); })
      .finally(() => bump(-1));
    return () => { live = false; };
  }, [s.divisionCode]);

  /* ── THE DIVISION'S OWN CONTACT US BLOCK (audit row 24) ─────────────────────
     Division-scoped, same as /refdata. Only an ASSEMBLED block is accepted
     (`ok && html && imgCount`, the monolith's guard at L11687) — the bare
     layoutblock shell has no ISC photograph in it and swapping that in would
     blank a block that currently works. A refusal is cached as "" so the route is
     asked once per division and the canvas settles on the cream fallback instead
     of flickering. */
  useEffect(() => {
    const div = String(s.divisionCode || "").toUpperCase();
    if (!div) { setIsc(null); return; }
    const hit = ISC_CACHE[div];
    if (hit !== undefined) { setIsc({ div, html: hit }); return; }
    let live = true;
    bump(1);
    getIscBlock(div)
      .then((d) => { ISC_CACHE[div] = d && d.ok && d.html && d.imgCount ? String(d.html) : ""; })
      .catch(() => { ISC_CACHE[div] = ""; })
      .finally(() => { if (live) setIsc({ div, html: ISC_CACHE[div] || "" }); bump(-1); });
    return () => { live = false; };
  }, [s.divisionCode]);

  /* ── EVERY DATA EXTENSION A BLOCK DECLARES, ONE ROW EACH (2026-09-02) ───────────
     Layne's Contact Us ask, generically: a block whose header declares `dataSource`
     fills its `fromColumn` fields from the row for the author's division. The sources
     come off the REGISTRY, not a list here — a data-driven block Layne authors next
     month is fetched the moment its header lands, with no change in this file.
     Division-scoped like /refdata and /sfmc-isc, and re-run when the registry version
     changes (the blocks arrive after first render; before that there is nothing to
     declare).

     ── A FAILED LOOKUP IS NOT A DIVISION WITH NO ROW (2026-09-09) ─────────────────
     This used to read `d && d.ok && d.found && d.row ? d.row : null` with a
     `.catch(() => null)`, which gave the SAME `null` to three different outcomes: a
     thrown fetch, a `!ok` response, and a division that genuinely has no row. The API
     never conflated them — `ok` and `found` are separate fields and its own comment
     says "`found:false` is a real answer (a division with no row), not an error" — the
     loss was here.

     It mattered twice. Live: during a gateway outage every division read as no-row, so
     every Contact Us block rendered the `none` treatment with an unsubstituted
     `{{ContactUrl}}` and nothing surfaced why. And it blocked the fix Layne asked for
     (BUG-CONTACTUS-TOKENS-NOT-RENDERED-20260909): a gate that STOPS a send when a
     division has no row is only safe while a broken lookup is a different value, or an
     outage becomes a company-wide send freeze. She flagged that risk from memory — "a
     broken lookup and a genuinely empty result were indistinguishable" — and she was
     describing this line, not a past one.

     `d.ok && !d.found` is the only path to `null` now. Everything that went wrong is
     `DE_FAILED`, which consumers treat as not-yet-answered. */
  const echVer = echVersion();
  useEffect(() => {
    const div = String(s.divisionCode || "").toUpperCase();
    const sources = echAllDataSources();
    if (!div || !sources.length) { setDeRows(null); return; }
    let live = true;
    bump(1);
    /* ── A ROW THAT NEVER COMES BACK IS A FAILED ROW, NOT A PENDING ONE (2026-09-11) ──
       Jack on emailbuilder-qa: the Done button read "Checking division data…" and never
       changed — the /sfmc-de-row calls on that environment's gateway did not answer at
       all (no response, no error), so `deRows` never landed and `dataGatePending` held
       the editor shut with no way out. A gateway that hangs is the same fact as one that
       500s: the row is unavailable. After DE_TIMEOUT_MS the call is treated as
       DE_FAILED, which the gate already knows how to show (a warning, not a lock). */
    const DE_TIMEOUT_MS = 12000;
    const timed = <T,>(p: Promise<T>): Promise<T> => new Promise((res, rej) => {
      const t = window.setTimeout(() => rej(new Error("de-row timeout")), DE_TIMEOUT_MS);
      p.then((v) => { window.clearTimeout(t); res(v); }, (e) => { window.clearTimeout(t); rej(e); });
    });
    Promise.all(sources.map((ds) => timed(getDeRow(ds.externalKey, div, ds.primaryKey))
      .then((d) => [ds.externalKey,
        !d || !d.ok ? DE_FAILED      /* the route answered with a failure */
          : d.found && d.row ? d.row /* the row */
            : null                   /* answered, and this division has no row */
      ] as const)
      .catch(() => [ds.externalKey, DE_FAILED] as const)))
      .then((pairs) => { if (live) setDeRows({ div, rows: Object.fromEntries(pairs) }); })
      .finally(() => bump(-1));
    return () => { live = false; };
  }, [s.divisionCode, echVer]);

  /* the Step-1 picks, with whatever Airtable knows about each one */
  const picks: EchPick[] = useMemo(() => {
    const rd = ref && ref.div === s.divisionCode ? ref.data : null;
    const idsC = (rd && rd.ids && rd.ids.communities) || {};
    const idsM = (rd && rd.ids && rd.ids.masterplans) || {};
    const bagC = (rd && rd.addr && rd.addr.communities) || {};
    const bagM = (rd && rd.addr && rd.addr.masterplans) || {};
    const one = (name: string, kind: "CM" | "MP"): EchPick => {
      /* looked up by record id, matching on KIND: a masterplan reads the Masterplans
         row, a community reads the Communities row. Mapping an MPC to one of its
         sub-collections would hand back that collection's address, not the
         masterplan's. (monolith __echRowFor) */
      const rid = (kind === "MP" ? idsM : idsC)[name] || (kind === "MP" ? idsM : idsC)[name.trim()] || "";
      const row = rid ? (kind === "MP" ? bagM : bagC)[rid] : undefined;
      return {
        name, kind,
        code: row?.code || codes[name.toLowerCase()] || undefined,
        pageUrl: row?.pageUrl, address: row?.address, city: row?.city,
        state: row?.state, zip: row?.zip,
        /* the Airtable Amenities multi-select — what the amenity picker preselects
           from when the requester hasn't chosen for themselves (audit row 17) */
        amenities: row?.amenities,
        market: String(row?.market || "").split("|")[0].split(/\s*-\s*/)[0].trim().toUpperCase() || undefined,
      };
    };
    const out = [
      ...names(s.community).map((n) => one(n, "CM")),
      ...names(s.masterplan).map((n) => one(n, "MP")),
    ];
    /* the page derived from the search hits' shared parent (see `mpDerived`) — only
       for the sole pick it was derived for, and only when refdata gave it none */
    if (out.length === 1 && mpDerived && mpDerived.key === String(out[0].name || "").toLowerCase() && !out[0].pageUrl) {
      out[0] = { ...out[0], kind: "MP", pageUrl: mpDerived.url };
    }
    /* ── A MASTERPLAN WITH NO STREET BORROWS ITS COLLECTION'S (Jack, 2026-08-20) ──
       Folded into the pick here rather than handled downstream, so every consumer —
       echCommInfo, the directions URL, Address Line 2 — sees one shape and none of
       them has to know an address was borrowed. `addrFrom` is the only tell, and the
       Inspector uses it to say whose street it is. `echBorrowAddr` gates itself on a
       single MP pick with no address of its own, so this is a no-op otherwise. */
    const borrowed = echBorrowAddr(out, bagC, idsC);
    if (borrowed) {
      out[0] = {
        ...out[0],
        address: borrowed.addr,
        city: out[0].city || borrowed.city,
        state: out[0].state || borrowed.state,
        zip: out[0].zip || borrowed.zip,
        addrFrom: borrowed.from,
      };
    }
    /* ── AND ITS AMENITIES, FOR THE SAME REASON (2026-08-21) ────────────────────
       The Masterplans table has no Amenities field at all, so a masterplan pick
       arrived with none and the amenity block came up empty — Jack hit this on JAX
       "Granary Park", whose four collections each carry nine amenities. Folded in
       here beside the address borrow so `echFillAll` needs no masterplan special
       case: by the time it runs, the pick simply HAS amenities, borrowed or its own.
       `amenitiesFrom` is the only tell. */
    const amen = echBorrowAmenities(out, bagC, idsC);
    if (amen) out[0] = { ...out[0], amenities: amen.amenities, amenitiesFrom: amen.from };
    return out;
  }, [s.community, s.masterplan, s.divisionCode, ref, codes, mpDerived]);


  /* ── SEVERAL COLLECTIONS OF ONE MASTERPLAN ARE STILL ONE PLACE (2026-08-27) ───
     Jack, on Step 1: "could not know like if i select multiple collections from the
     same mpc that it would say on community/mpc level?" They are not several
     communities — `Village - Timber Ridge` and `50' & 60' - Timber Ridge` are two
     collections of Timber Ridge — so the send has one identity and the old rule
     ("more than one pick → market") was throwing it away.

     `echSharedParent` answers null unless it is certain; see its own header. The
     masterplan it returns becomes the send's NAME, ADDRESS, PAGE and MARKET, while
     the INVENTORY keeps coming from `collections`/`collPacks` below, which are built
     from the picks themselves. `pack` is deliberately NOT reloaded for the parent —
     see `EchCtx.parent`.

     It borrows an address and amenities the same way a sole masterplan pick does, and
     for the same reason: the Masterplans table often carries neither, and the borrow
     helpers are the one place that rule lives. */
  const parent = useMemo(() => {
    const rd = ref && ref.div === s.divisionCode ? ref.data : null;
    if (!rd) return undefined;
    const idsC = (rd.ids && rd.ids.communities) || {};
    const idsM = (rd.ids && rd.ids.masterplans) || {};
    const bagC = (rd.addr && rd.addr.communities) || {};
    const bagM = (rd.addr && rd.addr.masterplans) || {};
    let mp = echSharedParent(picks, bagM, idsM);
    if (!mp) return undefined;
    const borrowed = echBorrowAddr([mp], bagC, idsC);
    if (borrowed) {
      mp = {
        ...mp,
        address: borrowed.addr,
        city: mp.city || borrowed.city,
        state: mp.state || borrowed.state,
        zip: mp.zip || borrowed.zip,
        addrFrom: borrowed.from,
      };
    }
    const amen = echBorrowAmenities([mp], bagC, idsC);
    if (amen) mp = { ...mp, amenities: amen.amenities, amenitiesFrom: amen.from };
    return mp;
  }, [picks, ref, s.divisionCode]);

  /* ── THE MASTERPLAN ABOVE A SINGLE PICK (2026-08-28) ──────────────────────────
     A different question from `parent` above and it must not be confused with it: for
     ONE community the community is still the send, and the masterplan is a second
     fact the Grand Opening blocks ask for by name. `echParentName` answers "" unless
     it is certain, and nothing but the NAME comes from it — address, page and market
     all still resolve from the community itself. Same bag, same memo inputs. */
  const parentName = useMemo(() => {
    const rd = ref && ref.div === s.divisionCode ? ref.data : null;
    if (!rd) return "";
    return echParentName(picks, (rd.addr && rd.addr.masterplans) || {}, (rd.ids && rd.ids.masterplans) || {});
  }, [picks, ref, s.divisionCode]);

  /* the single picked community's content pack. Step 1 stores a NAME; /sfmc-content
     is keyed by CODE, so resolve name → code once and cache it (monolith
     __lvResolveAll → __lvContentFor). Several picks have no one plan set, so no pack. */
  const soleName = picks.length === 1 ? picks[0].name : "";
  const soleCode = picks.length === 1 ? picks[0].code || "" : "";
  /* every state the request could legitimately be in: the pick's own row wins;
     else ALL of the division's markets' states plus its refdata rows' states
     (echGeo — MDH spans MD/VA/WV/DC). Recomputed as refdata lands. */
  const rdNow = ref && ref.div === s.divisionCode ? ref.data : null;
  const rowStates = Object.values((rdNow && rdNow.addr && rdNow.addr.communities) || {}).map((r) => (r as { state?: string }).state);
  const okStates: string[] = (picks.length === 1 && picks[0].state)
    ? [String(picks[0].state).toUpperCase()]
    : echDivisionStates(s.divisionCode, rowStates);
  const okKey = okStates.join(",");
  useEffect(() => {
    if (!soleName) { setPack(null); return; }
    const key = soleName.toLowerCase();
    if (pack && pack.key === key) {
      /* refdata landing after the pack can narrow the allowed states — re-check the
         pack already in hand rather than re-fetching it */
      const st = echStateOfUrl(pack.C && pack.C.community ? pack.C.community.url : "");
      if (pack.C && !echStateAllowed(st, okStates)) setPack({ key, C: null });
      return;
    }
    let live = true;
    bump(1);
    const load = (code: string) =>
      getCommunityPack(code)
        .then((C) => {
          if (!live) return;
          /* ── AND A PACK FROM ANOTHER STATE IS REFUSED EVEN WITH A CODE (2026-09-09) ──
             The url on the pack says where the community really is; when that state
             is not the request's, the resolver matched a namesake, and an empty
             card is the honest result — a Texas home under a California header is not. */
          const st = echStateOfUrl(C && C.community ? C.community.url : "");
          if (!echStateAllowed(st, okStates)) { setPack({ key, C: null }); return; }
          setPack({ key, C: C || null });
        })
        .catch(() => { if (live) setPack({ key, C: null }); })
        .finally(() => bump(-1));
    if (soleCode) { load(soleCode); return () => { live = false; }; }
    /* ── THE PICK'S NAME IS NOT ALWAYS SFMC'S NAME (2026-08-22) ────────────────
       Airtable's display name carries an audience tag SFMC has never heard of, so
       `?q=Edenbrooke at Hyland Trail | 55+` answered with nothing while the same
       search minus the tag returned both collections. Try the spellings in order —
       exact first, so a community that IS spelt the same is unaffected. */
    (async () => {
      for (const q of echNameVariants(soleName)) {
        const r = await searchCommunities(q).catch(() => null);
        const items = (r && r.items) || [];
        if (items.length) return { items };
      }
      return { items: [] as { code?: string; name?: string }[] };
    })()
      .then((r) => {
        /* ── A NAME SEARCH NEVER HANDS BACK SOMEONE ELSE'S COMMUNITY (2026-09-09) ──
           Katy, BAH, pick "Creekside" (Mountain House, CA — not in SFMC's search):
           `?q=Creekside` answered nine communities from three states, none of them
           hers, and this line took items[0] — Creekside Crossing, San Diego — so the
           email's header, "View Homes" button and home rows all pointed at another
           division. The wizard picker fixed the same fault on 2026-08-22 ("ONLY TAKE
           items[0] WHEN IT IS THE ONLY ITEM"); this resolver never got the rule.
           Exact name (either spelling) or a single hit; anything looser is no pack. */
        const items = r.items || [];
        const norm = (v: unknown) => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
        const hit = items.find((x) => String(x.name || "").toLowerCase() === key)
          || items.find((x) => norm(x.name) === norm(soleName))
          || (items.length === 1 ? items[0] : undefined);
        const code = hit ? String(hit.code) : "";
        if (!code) {
          /* no single community answers to this name — but its COLLECTIONS might
             (see `mpDerived`): every hit's url shares one parent whose slug is the name */
          const parent = echSharedParentUrl(soleName, items.map((x) => (x as { url?: string }).url));
          if (parent && live) {
            setMpDerived({ key, url: parent, kids: items.map((x) => ({ code: String(x.code || ""), name: String(x.name || "") })).filter((k) => k.code) });
          }
          if (live) setPack({ key, C: null });
          bump(-1);
          return;
        }
        setCodes((c) => (c[key] === code ? c : { ...c, [key]: code }));
        return load(code);
      })
      .catch(() => { if (live) setPack({ key, C: null }); bump(-1); });
    return () => { live = false; };
    /* `pack` is read to short-circuit a repeat load for the same community; adding it
       to the deps would re-run the effect on its own setState */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soleName, soleCode, okKey]);

  /* ── A MASTERPLAN'S PLANS LIVE IN ITS COLLECTIONS (Jack, 2026-08-20: "full plan
     should pull in all related collections") ────────────────────────────────────
     THE DEFECT. Driven live in Chrome: division ATL, focus "Communities /
     Masterplans", pick **Arlington Heights** → the Featured Plan panel correctly
     offers "Floorplans · 0 selected · Choose ›", the wizard opens, "Next: Floorplans
     ›" reaches "Choose floorplans" — and the step renders ZERO plan cards. Same with
     **Timber Ridge**. Both are masterplans whose own /sfmc-content pack carries no
     plans; every plan sits on a CHILD COLLECTION (`50' & 60' - Timber Ridge`,
     `Village - Timber Ridge`, `30' FE - Whispering Winds`, …). Two tries, two empty
     steps — the normal case for a masterplan, not an edge case.

     The collections come from `echCollectionsOf`, the SAME matcher `echBorrowAddr`
     uses (extracted for this, so the path rule cannot drift between the two callers).
     A plain community resolves to [] here and the whole branch is inert. */
  const collections = useMemo(() => {
    const rd = ref && ref.div === s.divisionCode ? ref.data : null;
    const idsC = (rd && rd.ids && rd.ids.communities) || {};
    const bagC = (rd && rd.addr && rd.addr.communities) || {};
    /* ── SEVERAL COMMUNITIES PICKED → THE PICKS THEMSELVES ARE THE GROUPS ──────
       (Jack, 2026-08-21: "if there's multiple then select multiple" — the listing
       table splits across them and the wizard picker groups by community.) Same
       Pack[] plumbing the masterplan collections already ride: each pick with a
       code becomes one group pack. Nothing else changes shape — a sole community
       keeps returning its collections (or nothing), byte-identical. */
    if (picks.length > 1) {
      /* ── A MASTERPLAN AMONG SEVERAL PICKS STILL ROLLS UP (2026-08-22) ─────────
         Jack, with Saddle Oaks + "Edenbrooke at Hyland Trail | 55+" + Holstein
         Crossing on the request: "selected multiple communities but yet you're only
         showing one." Two reasons, both here. A masterplan pick has no inventory of
         its own — its homes live on its collections — and this branch only kept
         picks that already carried a `code`, so a masterplan (whose refdata row
         often has none) was dropped silently. Now each pick contributes what it
         actually HAS: a community contributes itself, a masterplan contributes its
         collections, and a pick with neither is left for the name→code resolver. */
      const out: { code: string; name: string }[] = [];
      const seen: Record<string, 1> = {};
      const add = (code: string, name: string) => {
        const c = String(code || "");
        if (!c || seen[c]) return;
        seen[c] = 1; out.push({ code: c, name: String(name || "") });
      };
      picks.forEach((p) => {
        /* ── THE KIND ON THE PICK IS NOT AUTHORITATIVE (2026-08-22) ─────────────
           Step 1 offers communities and masterplans in ONE merged list, so a
           masterplan can arrive as `kind:"CM"`. Measured live: a JAX request for
           Hardwick Farms + Saddle Oaks + Shearwater rendered two headings —
           Hardwick is a masterplan (no code of its own, its homes live on
           "Townhomes - Hardwick Farms", code 7850) and, read as a community, it
           resolved to no code and was dropped without a word. So the roll-up is
           tried for ANY pick that has no code of its own, whatever kind it claims
           to be. A real community has no collections and falls through unchanged. */
        if (p.kind === "MP" || !p.code) {
          const kids = echCollectionsOf({ ...p, kind: "MP" }, bagC, idsC);
          if (kids.length) { kids.forEach((k) => add(k.code, k.name)); return; }
        }
        add(p.code || codes[String(p.name || "").toLowerCase()] || "", p.name || "");
      });
      return out.slice(0, 10);
    }
    /* ── AN AOI REQUEST'S COMMUNITIES ARE ITS GROUPS (2026-08-22) ──────────────
       Jack: "why can't you pull area of interest, it's in airtable under each
       community you can leverage." With `/refdata` schema 8 carrying each community's
       own AOI, an Area-of-Interest request resolves to the communities inside it and
       rides the exact plumbing a masterplan already uses — collPacks → the grouped
       listing fill → the picker's scope. Before this, an AOI send filled nothing
       below the header and the requester had to name communities by hand.
       Only when Step 1 named NO community: an explicit pick always wins over the
       geography it happens to sit in. */
    if (!picks.length) {
      const aoi = String(s.areaOfInterest || "").trim();
      if (aoi) return echCommsInAoi(aoi, bagC, idsC);
      return [];
    }
    if (picks.length !== 1) return [];
    const own = echCollectionsOf(picks[0], bagC, idsC);
    if (own.length) return own;
    /* refdata knows neither the masterplan nor its collections — the search hits do */
    if (mpDerived && mpDerived.key === String(picks[0].name || "").toLowerCase()) return mpDerived.kids;
    return [];
  }, [picks, ref, s.divisionCode, s.areaOfInterest, codes, mpDerived]);

  /* the collections' packs — ONE /sfmc-content per collection, in PARALLEL.
     Parallel and not sequential (the picker's own `wizConnect` port is sequential on
     purpose): there is no user waiting on a modal here, the fetch fires off the Step-1
     pick, and a masterplan has 2–5 collections rather than the picker's cap of 10.
     Keyed by the collection CODE LIST so a re-render with the same collections never
     refetches, and cancel-safe by the same `live` guard the sole pack uses — the
     `bump(-1)` stays OUTSIDE the guard so a cancelled fetch still clears `pending`. */
  const [collPacks, setCollPacks] = useState<{ key: string; packs: Pack[] } | null>(null);
  const collKey = collections.map((c) => c.code).join(",");
  /* the masterplan's own page, for the search fallback below — the pick's refdata url,
     else whatever the collections themselves sit under */
  const mpPage = picks.length === 1 ? String(picks[0].pageUrl || "") : "";
  useEffect(() => {
    if (!collKey) { setCollPacks(null); return; }
    let live = true;
    bump(1);
    const fetchAll = (list: { code: string; name?: string }[]) => Promise.all(list.map((c) =>
      getCommunityPack(c.code)
        /* THE AIRTABLE NAME IS THE GROUP HEADING, NOT SFMC'S (2026-08-20, decided
           after seeing both in Chrome). SFMC calls Timber Ridge's two collections
           "Timber Ridge 50'-60'" and "Village"; Airtable — and therefore Step 1's own
           "+ Add community or masterplan" list, which is where the requester just came
           from — calls them "50' & 60' - Timber Ridge" and "Village - Timber Ridge".
           A heading reading just "VILLAGE" makes the requester guess which Village.
           SFMC's name is kept as the fallback for a collection refdata can't name. */
        .then((C): Pack | null => (C ? { code: c.code, name: c.name || C.community?.name || "", C } : null))
        .catch(() => null)));
    const stocked = (rows: (Pack | null)[]) => rows.some((p) => !!p && !!p.C && ((p.C.homesites || []).length > 0 || (p.C.plans || []).length > 0));
    (async () => {
      let rows = await fetchAll(collections);
      /* ── REFDATA'S COLLECTION CODES ARE NOT ALWAYS SFMC'S (2026-09-11) ────────────
         Driven live on dev after #246, JAX → masterplan "Granary Park" → Available Now:
         the listing table read "No live SFMC content for Granary Park yet" while the
         inventory picker, one click away, listed eleven Granary Park homes. Refdata
         names the collections "40s / 50s / 60s - Granary Park" under codes 7354–7356;
         /sfmc-content for those codes carries no homes. SFMC's own search answers
         "Granary Park 40s" (07855) and "Granary Park 50s" (07347) — the packs the
         homes actually live in, and the ones a direct "50s - Granary Park" pick already
         resolves to by name. So when every refdata-coded pack is dry, the masterplan is
         searched by name (the same spellings the sole pick tries) and the hits that sit
         under its page stand in as the collections. Refdata is still asked first, and
         a stocked refdata answer is never second-guessed. */
      if (!stocked(rows) && soleName) {
        let items: { code?: string; name?: string; url?: string }[] = [];
        for (const q of echNameVariants(soleName)) {
          const r = await searchCommunities(q).catch(() => null);
          items = ((r && r.items) || []) as typeof items;
          if (items.length) break;
        }
        const parent = String(mpPage || echSharedParentUrl(soleName, items.map((x) => x.url)) || "").toLowerCase().replace(/[?#].*$/, "").replace(/\/+$/, "");
        const under = (u: unknown) => {
          const v = String(u || "").toLowerCase().replace(/[?#].*$/, "").replace(/\/+$/, "");
          return !!parent && v.indexOf(parent + "/") === 0 && v.slice(parent.length + 1).indexOf("/") < 0;
        };
        const kids = items.filter((x) => x.code && under(x.url)).map((x) => ({ code: String(x.code), name: String(x.name || "") }));
        if (kids.length && kids.map((k) => k.code).join(",") !== collKey) {
          const alt = await fetchAll(kids);
          if (stocked(alt)) rows = alt;
        }
      }
      if (live) setCollPacks({ key: collKey, packs: rows.filter(Boolean) as Pack[] });
    })().finally(() => bump(-1));
    return () => { live = false; };
    /* `collections` is re-derived on every ref/pick change; the CODE LIST is what
       actually decides whether a refetch is needed */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collKey]);

  /* ── MARKET AND DIVISION LEVEL FILL TOO (2026-08-22) ──────────────────────────
     Jack: "make sure you're doing division and market level as well … remember the
     hierarchy." Driven live before this, a Market-focus request (AZH / PHO) left the
     listing table on the template's placeholder rows — "Collection Name / 101 / Plan
     Name" — because nothing resolved a market or a division to communities. Community,
     masterplan and (as of today) AOI all did.
     WHICH communities, though: a division has ~50 and the table shows two groups of
     five. Alphabetical would be arbitrary and could miss every well-stocked one, so
     the ranking is the division's own available-home count — one `/homesites.json`
     call, already R2-cached, already the feed the direct picker uses. Communities come
     back ordered by how much live inventory they actually have, which is what a
     market-wide "Explore Homes" table is for.
     The packs are built from that same feed rather than a /sfmc-content call each:
     these are AUTO-FILL rows, not stored picks, so there is no key to drift (the
     stored-pick path still resolves against real packs — see echDirect.resolveDirect).
     A market with no live homes yields none, and the table keeps its own defaults. */
  /* ── AN AOI WITH NO LIVE HOMES FALLS BACK (2026-08-22) ────────────────────────
     Jack picked "Duval" and got the template's placeholder rows. Measured against
     the live feeds: exactly ONE JAX community is tagged Duval — "Reserve 60s -
     Seaton Creek" — and it carries 0 available homes (2 plans). So the AOI resolves
     correctly and still has nothing to put in a homes table.
     The table then falls back to the MARKET tier, where every row is labelled with
     its own community, so nothing claims to be in Duval that isn't. While the AOI's
     packs are still loading the AOI keeps the scope, so a Green-Cove-Springs-shaped
     request never flashes market-wide homes on its way in. */
  const aoiHasComms = ((): boolean => {
    const aoi = String(s.areaOfInterest || "").trim();
    if (!aoi) return false;
    const rd = ref && ref.div === s.divisionCode ? ref.data : null;
    const bagC = (rd && rd.addr && rd.addr.communities) || {};
    const idsC = (rd && rd.ids && rd.ids.communities) || {};
    if (!echCommsInAoi(aoi, bagC, idsC).length) return false;
    /* resolved, but is there anything IN them? unknown until the packs land */
    if (!collPacks || collPacks.key !== collKey) return true;
    return collPacks.packs.some((p) => p && p.C && (p.C.homesites || []).length > 0);
  })();

  const geoLevel = ((): "" | "market" | "division" => {
    const f = String(s.focus || "").trim().toLowerCase();
    if (picks.length) return "";                    /* an explicit pick always wins */
    /* ── AN AOI THAT MATCHES NO COMMUNITY FALLS BACK (2026-08-22) ───────────────
       Jack picked "Duval" and got the template's placeholder rows. The AOI PICKLIST
       (/refdata `areas`) and the AOIs the COMMUNITIES actually carry are two
       different vocabularies — JAX's 50 communities are tagged Jacksonville, St.
       Augustine, Green Cove Springs, Palm Coast, St Johns, and nothing is tagged
       Duval. The picklist is being narrowed to what can resolve (FormView), and
       this is the safety net: an AOI that resolves to nothing drops to the market /
       division tier rather than shipping placeholders. */
    const aoiOn = !!String(s.areaOfInterest || "").trim();
    if (aoiOn && aoiHasComms) return "";
    /* an AOI whose communities cannot fill a homes table drops to the MARKET tier —
       the focus word is "Area of Interest", so the switch below would otherwise
       return "" and nothing would fill at all (measured on JAX / Duval) */
    if (aoiOn) return "market";
    if (f === "division") return "division";
    if (f === "market" || f === "") return "market";
    return "";
  })();
  const geoMkt = String(Array.isArray(s.market) ? (s.market[0] || "") : (s.market || ""))
    .split(/\s*-\s*/)[0].trim().toUpperCase();
  const geoKey = geoLevel ? geoLevel + ":" + String(s.divisionCode || "").toUpperCase() + ":" + (geoLevel === "market" ? geoMkt : "") : "";
  const [geoPacks, setGeoPacks] = useState<{ key: string; packs: Pack[] } | null>(null);
  useEffect(() => {
    if (!geoKey || !s.divisionCode) { setGeoPacks(null); return; }
    let live = true;
    bump(1);
    fetchDirect("homes", String(s.divisionCode))
      .then((ps) => {
        if (!live) return;
        const inMkt = (p: Pack) => {
          if (geoLevel !== "market" || !geoMkt) return true;
          return (p.C.homesites || []).some((h) => String((h as { market?: string }).market || "").toUpperCase() === geoMkt);
        };
        const ranked = ps
          .filter((p) => (p.C.homesites || []).length && inMkt(p))
          .sort((a, b) => (b.C.homesites || []).length - (a.C.homesites || []).length)
          .slice(0, 10);
        setGeoPacks({ key: geoKey, packs: ranked });
        /* ── AND THEN THE REAL PACKS (2026-08-22) ────────────────────────────────
           Jack: "you're still not pulling anything in from a market level." It WAS
           filling — Silver Falls 50s (3 rows) and Avalon Woods (1) — but thin,
           because `/homesites.json` is a leaner feed than `/sfmc-content`: JAX's
           whole-division list held a handful of homes while Edenbrooke's own pack
           carries eight on its own. So the ranking still comes from the division
           feed (one cheap call, and it is what knows the market of each home) and
           then the top communities' FULL packs are fetched to fill the table.
           `feedCommunityPack` is the same page-level promise cache the wizard uses,
           so a community already in hand costs nothing. */
        const top = ranked.filter((p) => p.code).slice(0, 5);
        if (!top.length) return;
        Promise.all(top.map((p) => getCommunityPack(String(p.code))
          .then((C) => (C && (C.homesites || []).length ? { code: p.code, name: p.name, C } as Pack : p))
          .catch(() => p)))
          .then((full) => { if (live) setGeoPacks({ key: geoKey, packs: full }); });
      })
      .catch(() => { if (live) setGeoPacks({ key: geoKey, packs: [] }); })
      .finally(() => bump(-1));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoKey]);

  const division = useMemo(() => {
    const dv = String(s.divisionCode || "").toUpperCase();
    const row = (divRows || []).find((x) => String(x.code || "").toUpperCase() === dv);
    return row ? { code: row.code, name: row.name, phone: row.phone } : null;
  }, [divRows, s.divisionCode]);

  /* ── THE MARKET NAME AND ITS STATE (code review 2026-08-19 #2) ───────────────
     THE BUG THIS FIXES. `form.market` holds market CODES, not labels — FormView
     writes `market: [dm[0][0]]` where `divMarkets()` returns `[code, name]` pairs
     (FormView.tsx:281). This used to `split(" - ")` the entry as though it were a
     "CODE - Name" label, so "ATL" split to ["ATL"] and the NAME resolved to the
     CODE: a Market-scope send put "ATL" in the customer-facing email header, which
     is the defect Jack reported from the outside.

     Resolve the code against the division's own market list, then fall back to the
     master MARKETS list — the same two-step the monolith's `__brandMarketInfo`
     does (`(mks.find(x=>x[0]===code)||[])[1] || code`, index.html L10586–10587).
     A label form is still tolerated so nothing breaks if a caller hands one over.

     THE STATE follows the monolith's precedence exactly (L10589–10598): the
     community pack's `whcState`, then the first homesite's `state`, then the
     `MARKET_STATE` backstop — which is why the header reads "Atlanta, GA" and not
     "Atlanta". Ported, not invented (see requestData.ts). The per-market phone
     cache (`window.__mktPhone`) has no route in this repo, so the phone falls
     through to the division's, exactly as it does there. */
  const market = useMemo(() => {
    const raw = Array.isArray(s.market) ? s.market[0] : s.market;
    if (!raw) return null;
    const txt = String(raw).trim();
    const code = txt.split(/\s*-\s*/)[0].trim().toUpperCase();
    const mks = divMarkets(s.divisionCode);
    const hit = mks.find((x) => x[0] === code)
      || MARKETS.filter((m) => m.c === code).map((m) => [m.c, m.n] as [string, string])[0]
      /* a bare NAME with no code in front of it still resolves */
      || mks.find((x) => String(x[1]).toLowerCase() === txt.toLowerCase());
    const name = hit ? hit[1] : (txt.split(/\s*-\s*/).slice(1).join(" - ").trim() || code);
    const mc = hit ? hit[0] : code;
    const c = pack?.C?.community;
    const hs0 = (pack?.C?.homesites || [])[0];
    const state = c?.whcState || hs0?.state || MARKET_STATE[mc] || "";
    return { name, state: state || undefined, phone: c?.phone || undefined };
  }, [s.market, s.divisionCode, pack]);

  return useMemo<EchCtx>(() => ({
    focus: s.focus || "",
    divisionCode: s.divisionCode || "",
    picks,
    parent,
    parentName,
    aoiLabel: s.areaOfInterest || "",
    initiative: s.initiative || "",
    market,
    division,
    social,
    pack: pack && pack.key === soleName.toLowerCase() ? pack.C : null,
    /* the KEY has to match, same as `pack` and `iscHtml` above: a masterplan change
       would otherwise leave the previous masterplan's collections on the canvas for a
       frame, and their floorplans in the picker */
    /* the geo tier only speaks when community / masterplan / AOI resolved nothing —
       hierarchy order, most specific first */
    /* ── AND THE PACKS HAVE TO HAVE SOMETHING IN THEM (2026-08-22) ──────────────
       `packs.length` was the test, so an AOI that resolved to one community with
       ZERO available homes still won over the market tier and the table shipped
       Layne's placeholder rows (measured: JAX "Duval" → "Reserve 60s - Seaton
       Creek", 0 homes, 2 plans). Resolving a community is not the same as having
       inventory; the geo tier only gets skipped when the specific answer can
       actually fill something. */
    /* HOMES specifically: the visible defect is a homes table with nothing in it,
       and a community with plans but no available homes cannot fill one. A Featured
       PLAN block loses nothing — the market packs it falls back to carry plans too. */
    collPacks: (collPacks && collPacks.key === collKey
      && collPacks.packs.some((p) => p && p.C && (p.C.homesites || []).length))
      ? collPacks.packs
      : (geoPacks && geoPacks.key === geoKey ? geoPacks.packs : []),
    /* the DIVISION has to match, or a division change would swap the previous
       division's ISC photo onto the canvas for a frame */
    iscHtml: isc && isc.div === String(s.divisionCode || "").toUpperCase() ? isc.html : undefined,
    /* same key discipline as `iscHtml`: a division change must not serve the old rows */
    deRows: deRows && deRows.div === String(s.divisionCode || "").toUpperCase() ? deRows.rows : undefined,
    pending: inflight > 0,
  }), [s.focus, s.divisionCode, s.areaOfInterest, s.initiative, picks, parent, parentName, market, division, social, pack, soleName, collPacks, collKey, geoPacks, geoKey, isc, deRows, inflight]);
}
